package er.prateek.crossly.ui.coach

import androidx.compose.animation.AnimatedContent
import androidx.compose.animation.core.FastOutSlowInEasing
import androidx.compose.animation.core.RepeatMode
import androidx.compose.animation.core.animateFloat
import androidx.compose.animation.core.infiniteRepeatable
import androidx.compose.animation.core.rememberInfiniteTransition
import androidx.compose.animation.core.tween
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.animation.togetherWith
import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.gestures.awaitEachGesture
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxScope
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.GenericShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.zIndex
import androidx.compose.ui.geometry.CornerRadius
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Rect
import androidx.compose.ui.geometry.RoundRect
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Path
import androidx.compose.ui.graphics.PathFillType
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.layout.onSizeChanged
import androidx.compose.ui.layout.positionInRoot
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.IntOffset
import androidx.compose.ui.unit.IntSize
import androidx.compose.ui.unit.dp
import crossly.shared.generated.resources.Res
import crossly.shared.generated.resources.coach_play_got_it
import crossly.shared.generated.resources.coach_play_kicker
import crossly.shared.generated.resources.coach_play_next
import crossly.shared.generated.resources.coach_play_skip
import crossly.shared.generated.resources.coach_play_step
import er.prateek.crossly.ui.components.GameButton
import er.prateek.crossly.ui.components.GameButtonSize
import er.prateek.crossly.ui.components.GameButtonStyle
import er.prateek.crossly.ui.theme.Radius
import er.prateek.crossly.ui.theme.Spacing
import er.prateek.crossly.ui.theme.game
import er.prateek.crossly.ui.theme.mixWith
import org.jetbrains.compose.resources.stringResource
import kotlin.math.roundToInt

/**
 * Shared spotlight coach: a cutout over live UI plus a sticker card. Used on
 * play, levels, home resume — one physical language so the tour never feels
 * like a different app.
 *
 * [hole] is in root coordinates. [extra] is drawn in overlay-local space
 * (the play-screen letter toss).
 */
@Composable
fun CoachOverlay(
    stepIndex: Int,
    stepCount: Int,
    title: String,
    body: String,
    hole: Rect,
    onNext: () -> Unit,
    onSkip: () -> Unit,
    modifier: Modifier = Modifier,
    kicker: String = stringResource(Res.string.coach_play_kicker),
    holeCornerRadius: Dp = 16.dp,
    holePad: Dp = 8.dp,
    extra: @Composable BoxScope.(overlayOrigin: Offset) -> Unit = {},
) {
    val colors = MaterialTheme.game
    val density = LocalDensity.current
    var overlayOrigin by remember { mutableStateOf(Offset.Zero) }
    var cardSize by remember(stepIndex, title) { mutableStateOf(IntSize.Zero) }

    val pulse = rememberInfiniteTransition(label = "coachPulse")
    val pulseScale by pulse.animateFloat(
        initialValue = 1f,
        targetValue = 1.045f,
        animationSpec = infiniteRepeatable(
            animation = tween(900, easing = FastOutSlowInEasing),
            repeatMode = RepeatMode.Reverse,
        ),
        label = "coachPulseScale",
    )
    val pulseAlpha by pulse.animateFloat(
        initialValue = 0.55f,
        targetValue = 0.18f,
        animationSpec = infiniteRepeatable(
            animation = tween(900, easing = FastOutSlowInEasing),
            repeatMode = RepeatMode.Reverse,
        ),
        label = "coachPulseAlpha",
    )

    BoxWithConstraints(
        modifier = modifier
            .fillMaxSize()
            .onGloballyPositioned { overlayOrigin = it.positionInRoot() },
    ) {
        val overlaySize = with(density) { Size(maxWidth.toPx(), maxHeight.toPx()) }
        val padPx = with(density) { holePad.toPx() }
        val radiusPx = with(density) { holeCornerRadius.toPx() }
        val holeLocal = hole
            .toOverlayLocal(overlayOrigin)
            .inflate(padPx)
            .intersect(Rect(Offset.Zero, overlaySize))

        val cardWidthPx = with(density) {
            minOf(maxWidth - Spacing.xl * 2, 340.dp).toPx()
        }
        val estimatedCardHeight = with(density) { 196.dp.toPx() }
        val cardHeightPx = if (cardSize.height > 0) cardSize.height.toFloat() else estimatedCardHeight
        val gapPx = with(density) { Spacing.md.toPx() }
        val marginPx = with(density) { Spacing.md.toPx() }

        val placement = placeCoachTooltip(
            overlay = overlaySize,
            hole = holeLocal,
            cardWidth = cardWidthPx,
            cardHeight = cardHeightPx,
            gap = gapPx,
            margin = marginPx,
        )

        Canvas(modifier = Modifier.fillMaxSize()) {
            val scrim = Path().apply {
                fillType = PathFillType.EvenOdd
                addRect(Rect(Offset.Zero, size))
                if (!holeLocal.isEmpty) {
                    addRoundRect(
                        RoundRect(holeLocal, CornerRadius(radiusPx, radiusPx)),
                    )
                }
            }
            drawPath(scrim, colors.ink.copy(alpha = 0.58f))

            if (!holeLocal.isEmpty) {
                val ring = holeLocal.inflate(holeLocal.minDimension * (pulseScale - 1f) * 0.35f)
                drawRoundRect(
                    color = colors.gold.copy(alpha = pulseAlpha),
                    topLeft = ring.topLeft,
                    size = ring.size,
                    cornerRadius = CornerRadius(radiusPx + 4f, radiusPx + 4f),
                    style = Stroke(width = 3.5.dp.toPx()),
                )
                drawRoundRect(
                    color = colors.accent.copy(alpha = 0.9f),
                    topLeft = holeLocal.topLeft,
                    size = holeLocal.size,
                    cornerRadius = CornerRadius(radiusPx, radiusPx),
                    style = Stroke(width = 2.dp.toPx()),
                )
            }
        }

        Box(
            modifier = Modifier
                .fillMaxSize()
                .pointerInput(Unit) {
                    awaitEachGesture {
                        while (true) {
                            awaitPointerEvent()
                        }
                    }
                },
        )

        extra(overlayOrigin)

        Column(
            modifier = Modifier
                .zIndex(2f)
                .offset {
                    IntOffset(placement.origin.x.roundToInt(), placement.origin.y.roundToInt())
                }
                .width(with(density) { cardWidthPx.toDp() })
                .onSizeChanged { cardSize = it },
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            if (placement.showCaret && placement.caretOnTop) {
                CoachCaret(caretX = placement.caretX, pointingUp = true)
            }
            CoachCard(
                stepIndex = stepIndex,
                stepCount = stepCount,
                kicker = kicker,
                title = title,
                body = body,
                isLast = stepIndex >= stepCount - 1,
                onNext = onNext,
                onSkip = onSkip,
            )
            if (placement.showCaret && !placement.caretOnTop) {
                CoachCaret(caretX = placement.caretX, pointingUp = false)
            }
        }
    }
}

@Composable
private fun CoachCard(
    stepIndex: Int,
    stepCount: Int,
    kicker: String,
    title: String,
    body: String,
    isLast: Boolean,
    onNext: () -> Unit,
    onSkip: () -> Unit,
) {
    val colors = MaterialTheme.game
    val shape = RoundedCornerShape(Radius.card)

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .clip(shape)
            .background(colors.card)
            .border(1.5.dp, colors.cardBorder.mixWith(colors.gold, 0.35f), shape)
            .padding(Spacing.lg),
        verticalArrangement = Arrangement.spacedBy(Spacing.sm),
    ) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Text(
                text = kicker,
                style = MaterialTheme.typography.labelSmall,
                color = colors.gold,
            )
            CoachStepDots(current = stepIndex, total = stepCount)
        }

        AnimatedContent(
            targetState = Triple(stepIndex, title, body),
            transitionSpec = { fadeIn(tween(180)) togetherWith fadeOut(tween(120)) },
            label = "coachCopy",
        ) { (_, shownTitle, shownBody) ->
            Column(verticalArrangement = Arrangement.spacedBy(Spacing.xs)) {
                Text(
                    text = shownTitle,
                    style = MaterialTheme.typography.titleLarge,
                    color = colors.ink,
                )
                Text(
                    text = shownBody,
                    style = MaterialTheme.typography.bodyMedium,
                    color = colors.inkMuted,
                )
            }
        }

        if (stepCount > 1) {
            Text(
                text = stringResource(Res.string.coach_play_step, stepIndex + 1, stepCount),
                style = MaterialTheme.typography.labelSmall,
                color = colors.inkMuted,
            )
        }

        Row(
            modifier = Modifier.fillMaxWidth().padding(top = Spacing.xs),
            horizontalArrangement = Arrangement.spacedBy(Spacing.sm),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            GameButton(
                text = stringResource(Res.string.coach_play_skip),
                onClick = onSkip,
                style = GameButtonStyle.GHOST,
                size = GameButtonSize.SMALL,
            )
            GameButton(
                text = stringResource(
                    if (isLast) Res.string.coach_play_got_it else Res.string.coach_play_next,
                ),
                onClick = onNext,
                style = GameButtonStyle.GOLD,
                size = GameButtonSize.SMALL,
                fillWidth = true,
                modifier = Modifier.weight(1f),
            )
        }
    }
}

@Composable
private fun CoachStepDots(current: Int, total: Int) {
    val colors = MaterialTheme.game
    Row(horizontalArrangement = Arrangement.spacedBy(Spacing.xs), verticalAlignment = Alignment.CenterVertically) {
        repeat(total) { index ->
            val active = index == current
            Box(
                modifier = Modifier
                    .size(if (active) 8.dp else 6.dp)
                    .clip(CircleShape)
                    .background(if (active) colors.gold else colors.inkMuted.copy(alpha = 0.35f)),
            )
        }
    }
}

@Composable
private fun CoachCaret(caretX: Float, pointingUp: Boolean) {
    val colors = MaterialTheme.game
    val caretWidth = 18.dp
    val caretHeight = 10.dp
    val shape = GenericShape { size, _ ->
        if (pointingUp) {
            moveTo(size.width / 2f, 0f)
            lineTo(size.width, size.height)
            lineTo(0f, size.height)
        } else {
            moveTo(0f, 0f)
            lineTo(size.width, 0f)
            lineTo(size.width / 2f, size.height)
        }
        close()
    }
    Box(modifier = Modifier.fillMaxWidth().height(caretHeight)) {
        Box(
            modifier = Modifier
                .align(Alignment.CenterStart)
                .offset { IntOffset((caretX - caretWidth.toPx() / 2f).roundToInt(), 0) }
                .size(caretWidth, caretHeight)
                .clip(shape)
                .background(colors.card),
        )
    }
}
